import { useState } from "react";
import { FaX } from "react-icons/fa6";
import { FaArrowRight } from "react-icons/fa";
import { IoIosCheckboxOutline, IoIosTrophy } from "react-icons/io";
import { IoGameController } from "react-icons/io5";
import { ChallengeInfoResponse } from "@devour/client";
import useWindowSize from "@/hooks/useWindowSize";

// OnboardingSection type - matches backend ChallengeOnboardingSection model
interface OnboardingStep {
    title?: string;
    description?: string;
}

interface OnboardingSection {
    title?: string;
    description?: string;
    getStartedSteps?: OnboardingStep[];
    instantReward?: {
        title?: string;
        description?: string;
    };
    prizePool?: {
        title?: string;
        description?: string;
    };
}

/*
 * Extended challenge type - use type assertion instead of interface extension
 * to avoid conflicts with existing onboardingSection type
 */
type ExtendedChallenge = ChallengeInfoResponse & {
    onboardingSection?: OnboardingSection;
};

interface ChallengeHowToPlayProps {
    challenge: ChallengeInfoResponse;
}

// Default icons for steps (can be extended later to be configurable)
const STEP_ICONS = [
    IoIosCheckboxOutline,
    IoGameController,
    IoIosTrophy,
];

const HOW_TO_PLAY_DISMISSED_KEY = "challenge_how_to_play_dismissed";

/**
 * How To Play / Onboarding component for challenge pages.
 * Displays a dismissible banner with step-by-step instructions.
 *
 * Only renders if challenge.onboardingSection is configured with data.
 * Reads title from onboardingSection.title and steps from onboardingSection.getStartedSteps.
 */
export const ChallengeHowToPlay = ({ challenge }: ChallengeHowToPlayProps) => {
    const extendedChallenge = challenge as ExtendedChallenge;
    const windowWidth = useWindowSize()[0];
    const shouldDisplayArrows = windowWidth > 991;

    // Use challenge-specific key so dismissal is per-challenge
    const dismissKey = `${HOW_TO_PLAY_DISMISSED_KEY}_${challenge?.id || "default"}`;
    const [isDismissed, setIsDismissed] = useState(() => {
        return localStorage.getItem(dismissKey) === "true";
    });

    const handleDismiss = () => {
        localStorage.setItem(dismissKey, "true");
        setIsDismissed(true);
    };

    // Get onboarding section data
    const onboardingSection = extendedChallenge?.onboardingSection;
    const steps = onboardingSection?.getStartedSteps;
    const sectionTitle = onboardingSection?.title;
    const sectionDescription = onboardingSection?.description;

    // Hide section if no onboarding data is configured
    const hasOnboardingData = sectionTitle || steps && steps.length > 0;
    if (!hasOnboardingData || isDismissed) {
        return null;
    }

    return (
        <div className="challenge-page_orange-border">
            <div className="challenge-page_how-to-play-container">
                <div className="challenge-page_how-to-play-header">
                    <div className="challenge-page_how-to-play-cta">
                        {sectionTitle || "Get Started"}
                    </div>
                    <button
                        className="challenge-page_how-to-play-close"
                        onClick={handleDismiss}
                        aria-label={`Close ${sectionTitle || "onboarding"}`}
                    >
                        <FaX size={12} />
                    </button>
                </div>
                {sectionDescription &&
                    <div className="challenge-page_how-to-play-description">
                        {sectionDescription}
                    </div>
                }
                {steps && steps.length > 0 &&
                    <div className="challenge-page_how-to-steps-container">
                        {steps.map((step, index) =>
                            <HowToStepItem
                                key={index}
                                step={step}
                                icon={STEP_ICONS[index] || STEP_ICONS[STEP_ICONS.length - 1]}
                                showArrow={shouldDisplayArrows && index < steps.length - 1}
                            />)}
                    </div>
                }
            </div>
        </div>
    );
};

interface HowToStepItemProps {
    step: OnboardingStep;
    icon: React.ComponentType<{ className?: string }>;
    showArrow: boolean;
}

const HowToStepItem = ({ step, icon: Icon, showArrow }: HowToStepItemProps) => {
    return (
        <>
            <div className="challenge-page_how-to-step">
                <Icon className="challenge-page_how-to-step_icon" />
                <div className="challenge-page_how-to-step_title">
                    {step.title || "Step Title"}
                </div>
                <div className="challenge-page_how-to-step_description">
                    {step.description || "Complete this step to continue."}
                </div>
            </div>
            {showArrow &&
                <div className="challenge-page_how-to-step_arrow">
                    <FaArrowRight size={24} />
                </div>
            }
        </>
    );
};

export default ChallengeHowToPlay;
